import { measure } from '@/components/Analytics/Analytics';
import AchievementIcon from '@/components/Icon/AchievementIcon';
import CloseIcon from '@/components/Icon/CloseIcon';
import IconButton from '@/components/IconButton/IconButton';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import useAchievements, {
	AchievementNotificationPriority,
	CalculatedAchievement,
} from '@/hooks/useAchievements';
import useHaptics from '@/hooks/useHaptics';
import { Dialog } from '@headlessui/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleAchievementScore } from './AchievementListItem';

const MAX_SHOWN_NOTIFICATIONS = 3;

const AchievementNotifications = () => {
	const { achievements } = useAchievements();

	const [knownAchievements, setKnownAchievements] = useState(achievements);

	const achievementsToNotifyUserOf = useMemo(
		() =>
			achievements
				.filter((achievement) => {
					// If the notificationPriority is HIDDEN, don't notify the user
					if (
						achievement.notificationPriority ===
						AchievementNotificationPriority.HIDDEN
					) {
						return false;
					}

					// If the calculatedTotalScore is 0 or NaN, don't notify the user
					if (
						achievement.calculatedTotalScore === 0 ||
						Number.isNaN(achievement.calculatedTotalScore)
					) {
						return false;
					}

					const knownAchievement = knownAchievements.find(
						(originalAchievement) =>
							originalAchievement.id === achievement.id,
					);

					// If it's not known, notify the user
					if (!knownAchievement) {
						return true;
					}

					// If the score is higher, notify the user
					if (
						achievement.calculatedTotalScore >
						knownAchievement.calculatedTotalScore
					) {
						return true;
					}

					// Else, don't notify the user
					return false;
				})
				// Sort them by lowest score first, so the user sees the easiest ones first
				.sort((a, b) => a.calculatedTotalScore - b.calculatedTotalScore)
				// Sort them by the AchievementNotificationPriority
				.sort(
					(a, b) => b.notificationPriority - a.notificationPriority,
				),
		[achievements, knownAchievements],
	);

	const achievementToShow = useMemo(
		() => achievementsToNotifyUserOf[0],
		[achievementsToNotifyUserOf],
	);

	const hasAchievementToShow = useMemo(
		() => !!achievementToShow,
		[achievementToShow],
	);

	const dismissAchievement = useCallback(
		(achievement: CalculatedAchievement | undefined = undefined) => {
			if (!hasAchievementToShow) return;
			const achievementToDismiss = achievement ?? achievementToShow;

			// Replace it in the knownAchievements
			const newKnownAchievements = knownAchievements.map(
				(achievement) => {
					if (achievement.id === achievementToDismiss.id) {
						return achievementToDismiss;
					}
					return achievement;
				},
			);
			// Add it to the knownAchievements if it wasn't there
			if (!knownAchievements.includes(achievementToDismiss)) {
				newKnownAchievements.push(achievementToDismiss);
			}
			// Set the new knownAchievements
			setKnownAchievements(newKnownAchievements);
		},
		[achievementToShow, hasAchievementToShow, knownAchievements],
	);

	// Dismiss all remaining achievements they were not allowed to be shown
	useEffect(() => {
		if (achievementsToNotifyUserOf.length > MAX_SHOWN_NOTIFICATIONS) {
			// Add the rest to the knownAchievements (dismiss them)
			achievementsToNotifyUserOf
				.slice(MAX_SHOWN_NOTIFICATIONS)
				.forEach(dismissAchievement);
		}
	}, [achievementsToNotifyUserOf, dismissAchievement]);

	return (
		<AnimatePresence>
			{hasAchievementToShow && (
				<AchievementDialog
					key={achievementToShow.id}
					index={achievementsToNotifyUserOf.length - 1}
					achievement={achievementToShow}
					onClose={() => dismissAchievement()}
				/>
			)}
		</AnimatePresence>
	);
};

const AchievementDialog = ({
	index,
	achievement,
	onClose,
}: {
	index: number;
	achievement: CalculatedAchievement;
	onClose: () => void;
}) => {
	const reduceMotion = useReducedMotion();

	const { applyHapticNotification } = useHaptics();

	const colors: { bg: string; fg: string }[] = [
		{ bg: 'text-accent', fg: 'text-accentCompat' },
		{ bg: 'text-secondary', fg: 'text-white' },
	];

	// Take the index modulo the amount of colors
	const colorsToUse = colors[index % colors.length];

	// Measure achievement
	useEffect(() => {
		measure(
			`achievements-achieved-${achievement.id}-${achievement.calculatedAchievedTimes}-${achievement.calculatedTotalScore}`,
		);
	}, [achievement]);

	return (
		<Dialog static open onClose={onClose} className="relative z-50">
			<audio
				src={`/assets/sounds/achievement_${achievement.id}.mp3`}
				autoPlay
				aria-hidden
				className="hidden"
				onPlay={() => applyHapticNotification('success')}
			/>
			<motion.div
				className="fixed inset-0 flex size-full flex-col items-center justify-center gap-8 overflow-y-auto bg-black/60 p-4 pt-16 md:gap-16"
				initial={{
					opacity: 0,
				}}
				animate={{
					opacity: 1,
					transition: {
						delay: 1,
					},
				}}
				exit={{
					opacity: 0,
				}}>
				<div className="absolute inset-0 size-full bg-primary opacity-60" />
				<motion.div
					className={`${colorsToUse.fg} relative min-h-fit px-8 pb-8`}
					initial={{
						y: reduceMotion ? 0 : '-50%',
						opacity: 0,
					}}
					animate={{
						y: 0,
						opacity: [0, 0.8, 1],
						transition: {
							delay: 1.3,
							duration: 0.5,
							ease: 'backOut',
						},
					}}
					exit={{
						y: reduceMotion ? 0 : '80%',
						scale: reduceMotion ? 1 : 0.5,
						opacity: 0,
					}}
					whileTap={{
						scale: 0.9,
					}}
					onClick={onClose}>
					<LargeSquareBackground className={colorsToUse.bg} />
					<div className="relative grid items-center justify-center">
						<div className="-mt-4 text-center">
							<AchievementIcon className="mx-auto size-16 lg:size-16" />
							<p className="font-brand text-2xl">Challenge</p>
						</div>
						<div
							role="alert"
							className="flex flex-col items-center justify-center">
							<header className="text-center font-brand text-4xl">
								{achievement.title}
							</header>
							<p className="text-balance pb-4 text-center">
								{achievement.description}{' '}
								{achievement.calculatedExplanation}
							</p>
						</div>
						<div className="-mb-8">
							<SingleAchievementScore achievement={achievement} />
						</div>
					</div>
				</motion.div>
				<div className="mx-auto">
					<IconButton
						onClick={onClose}
						icon={<CloseIcon />}
						title="Sluiten"
						aria-label="Sluiten"
						animationDelay={2.5}
					/>
				</div>
			</motion.div>
		</Dialog>
	);
};

export default AchievementNotifications;
